import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecentlyViewedSync } from '../hooks/useRecentlyViewedSync';
import { Loader2, Layers, Grid3X3, Home } from 'lucide-react';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { FabricSwatchGallery } from '../components/configurator/fabric-gallery/FabricSwatchGallery';
import { PublicFabricBrowser } from '../components/fabric/PublicFabricBrowser';
import { FabricRoomPreview } from '../components/fabric/FabricRoomPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const FabricsPage = () => {
  useRecentlyViewedSync();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const [view, setView] = useState(['browser', 'gallery', 'room'].includes(params.get('view')) ? params.get('view') : 'browser');
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_URL}/fabric-collections/visible`)
      .then((res) => setBrands(res.data || []))
      .catch((err) => console.error('Failed to load fabrics:', err))
      .finally(() => setLoading(false));
  }, []);

  const configureWith = (entry) => {
    const product = (brands.find((b) => b.id === entry.brand_id)?.applicable_products || [])[0] || 'roller-shades';
    navigate(`/products/${product}?fabric=${entry.id}`);
  };

  return (
    <Layout>
      <section className="pt-32 pb-12 bg-neutral-50">
        <div className="container-xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="label-overline text-amber-700">Fabrics</span>
            <h1 className="mt-4 text-neutral-900">Fabric Collection</h1>
            <p className="mt-6 text-xl text-neutral-600 leading-relaxed">
              Browse our complete fabric library. Every texture with real swatch photos, article codes, and specifications. Click any two swatches to compare side-by-side.
            </p>
          </motion.div>
          {/* View toggle */}
          <div className="flex gap-2 mt-6">
            <button
              onClick={() => setView('browser')}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                view === 'browser' ? 'bg-amber-600 text-white' : 'bg-white text-neutral-600 border border-neutral-200 hover:border-amber-300'
              }`}
              data-testid="view-browser-btn"
            >
              <Layers className="w-4 h-4" />
              Browse by Collection
            </button>
            <button
              onClick={() => setView('gallery')}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                view === 'gallery' ? 'bg-amber-600 text-white' : 'bg-white text-neutral-600 border border-neutral-200 hover:border-amber-300'
              }`}
              data-testid="view-gallery-btn"
            >
              <Grid3X3 className="w-4 h-4" />
              Swatch Gallery
            </button>
            <button
              onClick={() => setView('room')}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                view === 'room' ? 'bg-amber-600 text-white' : 'bg-white text-neutral-600 border border-neutral-200 hover:border-amber-300'
              }`}
              data-testid="view-room-btn"
            >
              <Home className="w-4 h-4" />
              Room Preview
            </button>
          </div>
        </div>
      </section>

      <section className="section-padding" data-testid="fabrics-page">
        <div className="container-xl">
          {view === 'browser' ? (
            <PublicFabricBrowser />
          ) : view === 'room' ? (
            <FabricRoomPreview />
          ) : loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-amber-600" /></div>
          ) : (
            <FabricSwatchGallery brands={brands} onSelect={configureWith} selectLabel="Configure with this fabric" />
          )}
        </div>
      </section>
    </Layout>
  );
};

export default FabricsPage;
